import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import { isEmpty } from '@ember/utils';
import Component from '@glimmer/component';
import { PrismComboSelectInput } from '@mylighthouse/prism-ember';
import type { IconName } from '@mylighthouse/prism-ember/types/components/prism/icon';
import type { Option } from '@mylighthouse/prism-ember/types/components/prism/select-input';
import type { TriggerCentralMappingVariant } from 'lumen/components/settings/central-mapping/dropdowns/triggers/trigger-central-mapping';
import SettingsCentralMappingDropdownsTriggersTriggerCentralMapping from 'lumen/components/settings/central-mapping/dropdowns/triggers/trigger-central-mapping';
import type { Commissionable } from 'lumen/models/central-mapping/mapping-entry-market-segment';
import {
  AUTOMAPPING_STATUS,
  type AutomappingStatus,
  CENTRAL_MAPPING_COMMISSIONABLE_OPTION_ID,
} from 'lumen/utils/central-mapping-constants';

type DropdownCommissionableOption = Option & { value: Commissionable };

interface SettingsCentralMappingDropdownsDropdownCommissionableSignature {
  Args: {
    autoCommissionable: Commissionable;
    commissionable: Commissionable;
    hideAutomappingVisuals?: boolean;
    isMixed?: boolean;
    isReadOnly?: boolean;
    isSelected?: boolean;
    setCommissionable: (value: Commissionable) => void;
    userCommissionable: Commissionable;
    variant: TriggerCentralMappingVariant;
  };
}

export default class SettingsCentralMappingDropdownsDropdownCommissionable extends Component<SettingsCentralMappingDropdownsDropdownCommissionableSignature> {
  @service declare intl: Services['intl'];

  get breadcrumbs(): string[] {
    if (this.args.commissionable === null) {
      return [];
    }

    if (!this.selectedOption) {
      return [];
    }

    return [this.selectedOption.label];
  }

  get mappingStatus(): AutomappingStatus {
    const { userCommissionable, autoCommissionable, hideAutomappingVisuals } =
      this.args;

    if (!isEmpty(userCommissionable)) {
      return AUTOMAPPING_STATUS.USER_MAPPED;
    }

    if (!hideAutomappingVisuals && !isEmpty(autoCommissionable)) {
      return AUTOMAPPING_STATUS.AUTO_MAPPED;
    }

    return AUTOMAPPING_STATUS.UNMAPPED;
  }

  get options(): DropdownCommissionableOption[] {
    const { intl } = this;
    const { hideAutomappingVisuals, autoCommissionable } = this.args;
    const hasAutoMappedCommissionable = autoCommissionable !== null;

    const getIcon = (isAutoValue: boolean): IconName | undefined => {
      if (hideAutomappingVisuals) {
        return undefined;
      }
      return isAutoValue ? 'automation' : undefined;
    };

    const getUnsafeClassName = (isAutoValue: boolean): string | undefined => {
      if (hideAutomappingVisuals) {
        return undefined;
      }
      return isAutoValue ? 'mapping-dropdown__option--automation' : undefined;
    };

    const options: DropdownCommissionableOption[] = [
      {
        value: CENTRAL_MAPPING_COMMISSIONABLE_OPTION_ID.GROSS,
        label: intl.t('settings.central_mapping.gross'),
        icon: getIcon(
          autoCommissionable === CENTRAL_MAPPING_COMMISSIONABLE_OPTION_ID.GROSS,
        ),
        unsafe_className: getUnsafeClassName(
          autoCommissionable === CENTRAL_MAPPING_COMMISSIONABLE_OPTION_ID.GROSS,
        ),
      },
      {
        value: CENTRAL_MAPPING_COMMISSIONABLE_OPTION_ID.NET,
        label: intl.t('settings.central_mapping.net'),
        icon: getIcon(
          autoCommissionable === CENTRAL_MAPPING_COMMISSIONABLE_OPTION_ID.NET,
        ),
        unsafe_className: getUnsafeClassName(
          autoCommissionable === CENTRAL_MAPPING_COMMISSIONABLE_OPTION_ID.NET,
        ),
      },
      {
        value: CENTRAL_MAPPING_COMMISSIONABLE_OPTION_ID.NO,
        label: intl.t('general.no'),
        icon: getIcon(
          autoCommissionable === CENTRAL_MAPPING_COMMISSIONABLE_OPTION_ID.NO,
        ),
        unsafe_className: getUnsafeClassName(
          autoCommissionable === CENTRAL_MAPPING_COMMISSIONABLE_OPTION_ID.NO,
        ),
      },
    ];

    // Auto-mapped commissionable can't be unmapped.
    if (!hasAutoMappedCommissionable) {
      options.push({
        value: null,
        label: intl.t('general.unmapped'),
      });
    }

    return options;
  }

  get selectedOption(): DropdownCommissionableOption | null {
    return (
      this.options.find(
        (option) => option.value === this.args.commissionable,
      ) ?? null
    );
  }

  @action onChange(option: DropdownCommissionableOption): void {
    this.args.setCommissionable(option.value);
  }

  <template>
    <PrismComboSelectInput
      @disabled={{@isReadOnly}}
      @onChange={{this.onChange}}
      @options={{this.options}}
      @unsafe_triggerComponent={{component
        SettingsCentralMappingDropdownsTriggersTriggerCentralMapping
        breadcrumbs=this.breadcrumbs
        isMixed=@isMixed
        isReadOnly=@isReadOnly
        isSelected=@isSelected
        mappingStatus=this.mappingStatus
        variant=@variant
      }}
      @value={{this.selectedOption}}
      data-test-id="dropdown-commissionable"
    />
  </template>
}
